import {
  NEW_FACILITY_KEY,
  NewFacilityOption,
  storageEnvTypeOptions,
  WEIGHT_UNIT_TYPES_OPTIONs
} from "../../../../../config/constants";
import { IngredientDataTypesOption } from "../../../../../config/enums/IngredientType.enum";
import { MEASUREMENT_TYPES } from "../../../../../config/enums/MeasurementTypes";
import { StorageEnvType } from "../../../../../config/enums/StorageType.enum";
import {
  ExternalStorageFormValues,
  ExternalStorageSearchFacilityOption,
  ExternalStorageSelectOption,
  ExternalStorageToolVariant
} from "./externalStorage.types";
import { getExternalStorageVariantConfig } from "./externalStorageVariant.config";

export const isSpecificDataType = (dataType?: string) =>
  dataType === IngredientDataTypesOption.SPECIFIC;

export const buildExternalStorageNodeLabel = (externalStorageName: string) =>
  `External Warehouse (${externalStorageName})`;

export const mapDropdownFacilities = (
  facilities: any[] = [],
  includeNewFacility = false
): ExternalStorageSelectOption[] => {
  const list = includeNewFacility ? [...facilities, NewFacilityOption] : facilities;
  return list.map(item => ({
    key: item.value ?? item.id,
    value: item.value ?? item.id,
    text: item.text ?? item.title
  }));
};

export const mapSearchFacilities = (
  facilities: any[] = []
): ExternalStorageSearchFacilityOption[] =>
  facilities.map(item => ({
    id: item.id,
    title: item.title,
    value: item.id
  }));

export const getStorageEnvLabel = (value?: number) =>
  storageEnvTypeOptions.find(option => option.value === value)?.text || "";

export const getWeightUnitLabel = (value?: number) =>
  WEIGHT_UNIT_TYPES_OPTIONs.find(option => option.value === value)?.text || "";

export const buildDefaultFormValues = (
  currentData: any,
  defaultFacilityId?: string,
  variant: ExternalStorageToolVariant = "mapping"
): ExternalStorageFormValues => {
  const features = getExternalStorageVariantConfig(variant);
  const dataType = features.supportsDataType
    ? currentData?.dataType || IngredientDataTypesOption.SPECIFIC
    : IngredientDataTypesOption.SPECIFIC;

  return {
    externalStorageName: currentData?.externalStorageName || "",
    dataType,
    storageEnv: features.supportsStorageEnvironment
      ? currentData?.storageEnv || StorageEnvType.AMBIENT
      : StorageEnvType.AMBIENT,
    warehouseName: currentData?.facilityId || defaultFacilityId || "",
    storageTemperature: currentData?.storageTemperature ?? "",
    warehouseSize: currentData?.warehouseSize ?? "",
    weight: currentData?.weight ?? "",
    weightUnit: features.supportsWeightUnit
      ? currentData?.weightUnit || MEASUREMENT_TYPES.WEIGHT_KG
      : MEASUREMENT_TYPES.WEIGHT_KG,
    electricity: currentData?.electricity ?? "",
    warehouseUtilization:
      currentData?.warehouseUtilization ?? (isSpecificDataType(dataType) ? 100 : 0),
    storageDays: currentData?.storageDays ?? ""
  };
};

export const mapFormValuesToLoadedData = (
  values: ExternalStorageFormValues,
  storageId: string,
  variant: ExternalStorageToolVariant = "mapping"
) => {
  const features = getExternalStorageVariantConfig(variant);
  const specificDataType = features.supportsDataType
    ? isSpecificDataType(values.dataType)
    : true;

  return {
    _id: storageId,
    externalStorageId: storageId,
    externalStorageName: values.externalStorageName,
    facilityId: values.warehouseName,
    warehouseSize: specificDataType ? Number(values.warehouseSize) || 0 : 0,
    storageTemperature: Number(values.storageTemperature) || 0,
    weight: Number(values.weight) || 0,
    electricity: specificDataType ? Number(values.electricity) || 0 : 0,
    warehouseUtilization: specificDataType ? Number(values.warehouseUtilization) || 0 : 0,
    storageDays: Number(values.storageDays) || 0,
    dataType: values.dataType,
    storageEnv: features.supportsStorageEnvironment
      ? Number(values.storageEnv) || StorageEnvType.AMBIENT
      : StorageEnvType.AMBIENT,
    weightUnit: features.supportsWeightUnit
      ? Number(values.weightUnit) || MEASUREMENT_TYPES.WEIGHT_KG
      : MEASUREMENT_TYPES.WEIGHT_KG
  };
};

export interface BuildExternalStoragePayloadParams {
  values: ExternalStorageFormValues;
  variant: ExternalStorageToolVariant;
  ownerId?: string | null;
  sourceId?: string | null;
  supplierId?: string | null;
  supplierIngredientId?: string | null;
  facilityId: string;
  externalStorageId?: string | null;
}

export const buildExternalStoragePayload = ({
  values,
  variant,
  ownerId,
  sourceId,
  supplierId,
  supplierIngredientId,
  facilityId,
  externalStorageId
}: BuildExternalStoragePayloadParams) => {
  const features = getExternalStorageVariantConfig(variant);
  const specificDataType = features.supportsDataType
    ? isSpecificDataType(values.dataType)
    : true;

  const payload: Record<string, unknown> = {
    externalStorageName: values.externalStorageName,
    warehouseName: values.warehouseName || facilityId,
    storageTemperature: Number(values.storageTemperature) || 0,
    weight: Number(values.weight) || 0,
    storageDays: Number(values.storageDays) || 0,
    facilityId
  };

  if (features.supportsDataType) {
    payload.dataType = values.dataType || IngredientDataTypesOption.SPECIFIC;
    payload.warehouseSize = specificDataType ? Number(values.warehouseSize) || 0 : 0;
    payload.electricity = specificDataType ? Number(values.electricity) || 0 : 0;
    payload.warehouseUtilization = specificDataType
      ? Number(values.warehouseUtilization) || 0
      : 0;
  } else {
    payload.warehouseSize = Number(values.warehouseSize) || 0;
    payload.electricity = Number(values.electricity) || 0;
    payload.warehouseUtilization = Number(values.warehouseUtilization) || 0;
  }

  if (features.supportsStorageEnvironment) {
    payload.storageEnv = Number(values.storageEnv) || StorageEnvType.AMBIENT;
  }

  if (features.supportsWeightUnit) {
    payload.weightUnit = Number(values.weightUnit) || MEASUREMENT_TYPES.WEIGHT_KG;
  }

  if (variant === "supplier") {
    payload.ingredientId = supplierIngredientId;
    payload.supplierId = supplierId;
  } else {
    payload.ownerId = ownerId;
    payload.sourceId = sourceId;
    if (variant === "simulate") {
      payload.changeStatus = true;
    }
  }

  if (externalStorageId) {
    payload.externalStorageId = externalStorageId;
  }

  return payload;
};

export const isNewFacilitySelection = (value?: string) => value === NEW_FACILITY_KEY;
